import { set } from 'date-fns';
import React from 'react';
import ReactDatePicker from 'react-datepicker';
import styled, { css } from 'styled-components';

import { fjButtonTypes } from 'components/primitives';
// Untyped .js icon modules resolve as `any`.
import LockIcon from 'icons/lock';
import EditIcon from 'icons/pencil-bordered';
// `utils` is an untyped .js barrel; formatDate/parseDate resolve as `any`.
import { formatDate, parseDate } from 'utils';

const START_OF_DAY = 'START_OF_DAY';
const END_OF_DAY = 'END_OF_DAY';

type SetValues = Parameters<typeof set>[1];
type TimeOffset = typeof START_OF_DAY | typeof END_OF_DAY | SetValues;

type DateInput = Date | string | number | null;

// Props consumed directly by this component; any remaining props are forwarded
// to ReactDatePicker via the `...props` rest (mirrors the original JS).
interface DatePickerProps {
  size?: number;
  className?: string;
  displayEditOnHover?: boolean;
  inline?: boolean;
  isClearable?: boolean;
  selected?: DateInput;
  minDate?: DateInput;
  maxDate?: DateInput;
  dataTestId?: string;
  onChange?: (date: Date | null) => void;
  time?: TimeOffset;
  dateContainer?: React.ElementType;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
}

type DatePickerComponent = React.ForwardRefExoticComponent<
  DatePickerProps & React.RefAttributes<ReactDatePicker>
> & {
  START_OF_DAY: typeof START_OF_DAY;
  END_OF_DAY: typeof END_OF_DAY;
};

interface CustomInputProps {
  value?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}

const DatePicker = React.forwardRef<ReactDatePicker, DatePickerProps>(
  (
    {
      size,
      className,
      displayEditOnHover,
      inline,
      isClearable,
      selected,
      minDate,
      maxDate,
      dataTestId = 'isolated',
      onChange,
      time,
      dateContainer = (props: React.HTMLAttributes<HTMLSpanElement>) => <span {...props} />,
      placeholder,
      readOnly,
      disabled,
      ...props
    },
    ref,
  ) => {
    const datePickerRef = ref;

    const clear = (): void => {
      if (onChange) {
        onChange(null);
      }
    };

    const setTimeOffset = (date: Date | null): Date | null => {
      const startOfDayTime = { hours: 0, minutes: 0, seconds: 0 };
      const endOfDayTime = { hours: 23, minutes: 59, seconds: 59 };

      if (time === START_OF_DAY) return set(date as Date, startOfDayTime);
      if (time === END_OF_DAY) return set(date as Date, endOfDayTime);
      if (time) return set(date as Date, time);
      return date;
    };

    const CustomInput = React.forwardRef<HTMLButtonElement, CustomInputProps>(
      ({ value, onClick }, customRef) => {
        const pickerProps = {
          onClick,
          size,
          dataTestId,
          dateContainer,
          placeholder,
          readOnly,
          disabled,
        };
        return inline ? (
          <DatePickerInlineInput
            ref={customRef}
            value={value}
            displayEditOnHover={displayEditOnHover}
            {...pickerProps}
          />
        ) : (
          <DatePickerInput
            ref={customRef}
            value={formatDate(value)}
            handleClear={clear}
            {...pickerProps}
          />
        );
      },
    );

    return (
      <DatePickerContainer
        className={className}
        inline={inline}
        data-testid={`${dataTestId}-date-picker-container`}
        role="menu"
      >
        <ReactDatePicker
          {...props}
          ref={datePickerRef}
          onChange={(date: Date | null) => onChange!(setTimeOffset(date))}
          fixedHeight
          dateFormat="Pp"
          selected={parseDate(selected ?? null)}
          minDate={parseDate(minDate ?? null) as Date | undefined}
          maxDate={parseDate(maxDate ?? null) as Date | undefined}
          customInput={<CustomInput />}
        >
          {isClearable && !!(props as { selected?: unknown }).selected && (
            <ClearTextButton onClick={clear} buttonType={fjButtonTypes.SM}>
              Clear Date
            </ClearTextButton>
          )}
        </ReactDatePicker>
      </DatePickerContainer>
    );
  },
) as DatePickerComponent;

DatePicker.START_OF_DAY = START_OF_DAY;
DatePicker.END_OF_DAY = END_OF_DAY;

const ClearTextButton = styled.a<{ buttonType?: string }>`
  margin-top: 2px;
  margin-bottom: 10px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-style: solid;
  border-width: 1px;
  border-radius: 3px;
  text-decoration: none;
  font-size: ${({ theme }) => theme.fontSizes.p};
  color: ${({ theme }) => theme.colors.selectableLightBg};
  border-color: ${({ theme }) => theme.colors.selectableLightBg};
  height: ${({ theme }) => theme.grid.units(1)};

  &:hover {
    background-color: ${({ theme }) => theme.colors.selectableLightBg};
    color: ${({ theme }) => theme.colors.white};
    transition: 0.2s ease;
    text-decoration: none;
    cursor: pointer;
  }
`;

const ClearButton = styled(
  (props: React.SVGProps<SVGSVGElement> & { 'data-testid'?: string }) => (
    <svg viewBox="0 0 24 24" {...props}>
      <path d="m12 0c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm5.5 16.084-1.403 1.416-4.09-4.096-4.102 4.096-1.405-1.405 4.093-4.092-4.093-4.098 1.405-1.405 4.088 4.089 4.091-4.089 1.416 1.403-4.092 4.087z" />
    </svg>
  ),
)`
  fill: ${({ theme }) => theme.colors.placeholderText};
  width: 15px;
  position: absolute;
  right: 4px;
  cursor: pointer;
`;

const DatePickerContainer = styled.div<{ inline?: boolean }>`
  .react-datepicker-popper .react-datepicker {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  ${({ inline }) =>
    inline &&
    css`
      display: flex;
      flex: 1;

      > div,
      .react-datepicker-wrapper,
      .react-datepicker__input-container {
        display: flex;
        flex: 1;
      }
    `}
  .react-datepicker__day {
    &--selected,
    &--keyboard-selected {
      background-color: ${({ theme }) => theme.colors.selectableLightBg};
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.selected};
    }
  }
`;

interface DatePickerInputBaseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  dateContainer: React.ElementType;
  value?: string;
  placeholder?: string;
  handleClear?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  dataTestId?: string;
  size?: number;
}

const DatePickerInput = styled(
  React.forwardRef<HTMLButtonElement, DatePickerInputBaseProps>(
    (
      {
        dateContainer: Container,
        value,
        placeholder,
        handleClear,
        disabled,
        readOnly,
        dataTestId,
        ...props
      },
      ref,
    ) => (
      <button
        type="button"
        ref={ref}
        data-testid={`${dataTestId}-date-picker-input-button`}
        {...props}
      >
        {value ? <Container>{value}</Container> : placeholder}
        {!!value && !disabled && !readOnly && (
          <ClearButton
            onClick={handleClear}
            data-testid={`${dataTestId}-date-picker-clear-button`}
          />
        )}
      </button>
    ),
  ),
)`
  display: flex;
  align-items: center;
  font-size: ${({ theme, value }) => (value ? theme.fontSizes.p : '86%')};
  letter-spacing: ${({ value }) => (value ? 'auto' : '.03em')};
  border-width: 0.5px;
  width: ${({ theme: { grid }, size }) => grid.units(size || 5)};
  color: ${({ theme, disabled, value }) =>
    theme.colors[(disabled && 'buttonDisabled') || (!value && 'placeholderText') || 'black']};
  border-color: ${(props) =>
    props.theme.colors[props.disabled ? 'buttonDisabled' : 'selectableLightBg']};
  background-color: ${(props) =>
    props.theme.colors[props.disabled ? 'buttonDisabledBg' : 'transparent']};
  border-style: solid;
  height: ${({ theme: { grid } }) => grid.units(1.5)};
  padding: 5px 10px;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding-right: ${({ value }) => (value ? 30 : 10)}px;
  pointer-events: ${({ disabled, readOnly }) => (disabled || readOnly ? 'none' : 'auto')};
`;

interface DatePickerInlineInputBaseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  dateContainer: React.ElementType;
  value?: string;
  placeholder?: string;
  displayEditOnHover?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  dataTestId?: string;
  size?: number;
  // Referenced in the css block below but never passed at runtime (kept for
  // behavioral parity with the original JS).
  placeholderText?: boolean;
}

const DatePickerInlineInput = styled(
  React.forwardRef<HTMLButtonElement, DatePickerInlineInputBaseProps>(
    (
      {
        dateContainer: Container,
        value,
        placeholder,
        displayEditOnHover,
        readOnly,
        disabled,
        onClick,
        dataTestId,
        ...props
      },
      ref,
    ) => (
      <button
        type="button"
        ref={ref}
        {...props}
        disabled={disabled || readOnly}
        onClick={onClick}
        data-testid={`${dataTestId}-inline-input-button`}
      >
        {value ? <Container>{value}</Container> : placeholder}
        {readOnly ? (
          <StyledLockIcon displayOnHover={displayEditOnHover} />
        ) : (
          <StyledEditIcon displayOnHover={displayEditOnHover} onClick={onClick} />
        )}
      </button>
    ),
  ),
)`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: ${({ readOnly, value }) => (readOnly && !value ? 'center' : 'space-between')};
  font-size: ${({ theme }) => theme.fontSizes.p};
  color: ${({ theme, value }) => theme.colors[(!value && 'placeholderText') || 'black']};
  cursor: ${({ placeholderText, value }) => (!placeholderText && !value ? 'auto' : 'text')};
  background-color: transparent;
  border: 0;
  padding: 0;
  pointer-events: ${({ disabled, readOnly }) => (disabled || readOnly ? 'none' : 'auto')};

  > p {
    margin: 0;
    opacity: ${(props) => (props.displayEditOnHover && !props.value ? '0' : '1')};
    transition: opacity 100ms;
  }

  &:hover > p {
    opacity: 1;
  }
`;

const StyledEditIcon = styled(EditIcon).attrs((props) => ({
  style: {
    primaryColor: props.theme.colors.selectableLightBg,
    bgColor: 'transparent',
  },
  size: '16px',
}))<{ displayOnHover?: boolean }>`
  margin-left: 5px;
  cursor: pointer;
  opacity: ${(props) => (props.displayOnHover ? '0' : '1')};
  transition: opacity 100ms;

  ${DatePickerInlineInput}:hover & {
    opacity: 1;
  }
`;

const StyledLockIcon = styled(LockIcon).attrs((props) => ({
  style: {
    primaryColor: props.theme.colors.disabledText,
    bgColor: 'transparent',
  },
  size: '12px',
}))<{ displayOnHover?: boolean }>`
  margin-left: 5px;
  cursor: default;
  opacity: ${(props) => (props.displayOnHover ? '0' : '1')};
  transition: opacity 100ms;

  ${DatePickerInlineInput}:hover & {
    opacity: 1;
  }
`;

export default DatePicker;
